import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { callAPI } from '../services/api.service';
import { RootState } from '../store/store';
import { UserRoleEnum } from '../interfaces/user.interface';

interface VPCStatus {
  vpcRequired: boolean;
  vpcComplete: boolean;
  vpcShareRequired: boolean;
  teacherState?: string;
  teacherEmail?: string;
  message?: string;
}

export const useStudentVPCStatus = () => {
  const [vpcStatus, setVpcStatus] = useState<VPCStatus>({
    vpcRequired: false,
    vpcComplete: false,
    vpcShareRequired: false,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const user = useSelector((state: RootState) => state.user.user);

  const checkVPCStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Only check VPC for students
      if (!user?.email || user?.role !== UserRoleEnum.student) {
        setVpcStatus({
          vpcRequired: false,
          vpcComplete: true,
          vpcShareRequired: true,
        });
        setLoading(false);
        return;
      }

      const response = await callAPI('api/vpc/student/status');
      
      if (response?.status === 200) {
        setVpcStatus({
          vpcRequired: response.vpcRequired || false,
          vpcComplete: response.vpcComplete || false,
          vpcShareRequired: response.vpcShareRequired || false,
          teacherState: response.teacherState,
          teacherEmail: response.teacherEmail,
          message: response.message
        });
      } else {
        setError(response?.error || 'Failed to check VPC status');
        // Default to not required on error
        setVpcStatus({
          vpcRequired: false,
          vpcComplete: false,
          vpcShareRequired: false
        });
      }
    } catch (error: any) {
      console.error('Error checking VPC status:', error);
      setError(error?.message || 'Failed to check VPC status');
      // Default to not required on error
      setVpcStatus({
        vpcRequired: false,
        vpcComplete: false,
        vpcShareRequired: false
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.email) {
      checkVPCStatus();
    }
  }, [user?.email, user?.role]);

  // Helper functions for common checks
  const isVPCBlocked = () => {
    return vpcStatus.vpcRequired && !vpcStatus.vpcComplete;
  };

  const isVPCShareBlocked = () => {
    return vpcStatus.vpcShareRequired && !vpcStatus.vpcComplete;
  };

  const getVPCMessage = (context: 'upload' | 'view' | 'purchase') => {
    if (!isVPCBlocked()) return '';
    
    const contextMessages = {
      upload: 'Photo upload requires parental consent',
      view: 'Book viewing requires parental consent',
      purchase: 'Book purchase requires parental consent'
    };
    
    return contextMessages[context];
  };

  return {
    ...vpcStatus,
    loading,
    error,
    checkVPCStatus,
    isVPCBlocked,
    isVPCShareBlocked,
    getVPCMessage
  };
}; 